<script setup lang="ts">
import { computed, ref } from 'vue'
import { NButton, NInput } from 'naive-ui'
import type { AgentChannelSummary } from '../../stores/agent_channels'
import { useChannelActions } from '../../composables/useChannelActions'

const props = defineProps<{ channel: AgentChannelSummary }>()

const actions = useChannelActions()
const draft = ref('')
const sending = ref(false)
const wokenSessions = ref<number | null>(null)

const canSend = computed(() => !props.channel.archived && !sending.value && draft.value.trim() !== '')

async function send(): Promise<void> {
  if (!canSend.value) return
  sending.value = true
  const posted = await actions.post(props.channel, draft.value.trim(), (count) => {
    wokenSessions.value = count
  })
  sending.value = false
  if (posted) draft.value = ''
}

function onEnter(event: KeyboardEvent): void {
  if (event.isComposing) return
  void send()
}
</script>

<template>
  <form class="flex flex-col gap-1" @submit.prevent="send">
    <div class="flex gap-2 min-w-0">
      <NInput
        v-model:value="draft"
        class="flex-1 min-w-0"
        type="textarea"
        :autosize="{ minRows: 1, maxRows: 5 }"
        :disabled="channel.archived"
        :placeholder="channel.archived ? 'Archived channels are read-only' : 'Post as owner…'"
        data-testid="post-input"
        @keydown.enter.exact.prevent="onEnter"
      />
      <NButton type="primary" attr-type="submit" :disabled="!canSend" :loading="sending" data-testid="post-submit">
        Send
      </NButton>
    </div>
    <p v-if="wokenSessions === 0" class="text-xs text-amber-300 m-0" data-testid="no-session-woken">
      No active session — @mention one to wake it.
    </p>
    <p class="text-xs text-white/60 m-0">Enter to send · Shift+Enter for a newline.</p>
  </form>
</template>
